import { Injectable } from '@angular/core';
import { IAppSession, IHandleCustomerValidation } from '../../core/models/app.model';
import { APP_ROUTES as ROUTES } from '../../app.routes';
import { EONBOARDING_STEPS } from '../../core/enums/onboarding.enum';
import {
  IAggregatorValidationResponse,
  ICustomerValidationResponse,
  IMaxInstallmentValidationResponse,
} from '../../core/models/customer.model';
import { EVALIDATION_STATUS as STATUS, EVALIDATOR_COMPLETED } from '../../core/enums/customer.enum';
import { EERROR_PAGE_TYPES } from '@app/core/models/error.model';
import { ERROR_COMPONENT_CONSTANTS } from './error/error.constants';

@Injectable({
  providedIn: 'root',
})
export class ValidationsService {
  constructor() {}

  public customerValidationBlockingErrors(
    customerValidation: ICustomerValidationResponse
  ): IHandleCustomerValidation {
    const {
      can_request_validator,
      is_rejected_by_payments,
      is_blocked_by_signup_attempts,
      is_rejected_by_offer,
      validation_status,
      validator_completed,
      can_request_loan,
    } = customerValidation as ICustomerValidationResponse;

    if (!can_request_validator && !can_request_loan) {
      if (!!is_rejected_by_payments) {
        return {
          error: true,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_10],
        };
      }

      if (!!is_blocked_by_signup_attempts) {
        return {
          error: true,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_24],
        };
      }

      if (
        validation_status === STATUS.REJECTED &&
        validator_completed !== EVALIDATOR_COMPLETED.BIOMETRICS
      ) {
        if (!is_rejected_by_offer) {
          return {
            error: true,
            componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_28],
          };
        }

        if (!!is_rejected_by_offer) {
          return {
            error: true,
            componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_29],
          };
        }
      } else if (
        validation_status === STATUS.REJECTED &&
        validator_completed === EVALIDATOR_COMPLETED.BIOMETRICS
      ) {
        return {
          error: true,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_01],
        };
      }

      if (validation_status === STATUS.ERROR) {
        return {
          error: true,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_01],
        };
      }

      return {
        error: true,
        componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_27],
      };
    } else {
      return {
        error: false,
        route: ROUTES.VALIDATIONS,
        step: EONBOARDING_STEPS.USER_INFO,
      };
    }
  }

  public customerValidationRetryErrors(
    customerValidation: ICustomerValidationResponse
  ): IHandleCustomerValidation {
    const {
      can_request_validator,
      is_rejected_by_payments,
      is_blocked_by_signup_attempts,
      is_rejected_by_offer,
      validation_status,
      validator_completed,
      can_request_loan,
    } = customerValidation as ICustomerValidationResponse;

    if (!!can_request_validator && !!validator_completed) {
      if (validator_completed === EVALIDATOR_COMPLETED.BURO) {
        return {
          error: false,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_30],
        };
      }

      if (validator_completed === EVALIDATOR_COMPLETED.KEY_NUA) {
        return {
          error: false,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_31],
        };
      }

      if (validator_completed === EVALIDATOR_COMPLETED.LLEIDA) {
        return {
          error: false,
          componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_32],
        };
      }
    } else {
      return {
        error: false,
        route: ROUTES.VALIDATIONS,
        step: EONBOARDING_STEPS.USER_INFO,
      };
    }
  }

  public handleAggregatorValidations(
    aggregatorValidation: IAggregatorValidationResponse
  ): IHandleCustomerValidation {
    const { can_aggregator_capacity } =
      aggregatorValidation as IAggregatorValidationResponse;

    if (!can_aggregator_capacity) {
      //AGGREGATOR CAPACITY ERROR
      return {
        error: true,
        componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_19],
      };
    }
    return {
      error: true,
      componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_10],
    };
  }

  public handleMaxInstallmentValidations(
    maxInstallmentValidation: IMaxInstallmentValidationResponse
  ): IHandleCustomerValidation {
    const { have_offers, have_other_installments, can_have_max_installment } =
      maxInstallmentValidation as IMaxInstallmentValidationResponse;

    // CASE 2 MAX INSTALLMENT - REDIRECT TO OFFERS
    if (!can_have_max_installment && have_other_installments && have_offers) {
      return {
        error: false,
        componentData: null,
        route: ROUTES.WAITING_ROOM,
        step: EONBOARDING_STEPS.USER_INFO,
      };
    }

    // CASE 3 MAX INSTALLMENT - CONSULT SCORE AND SHOW 2 ERRORS
    if (!can_have_max_installment && !have_other_installments && have_offers) {
      return {
        error: false,
        componentData: null,
        route: ROUTES.NO_AVAILABLE_OFFER,
        step: EONBOARDING_STEPS.USER_INFO,
      };
    }

    // CASE 4 MAX INSTALLMENT - ALL FLAGS IN FALSE
    return {
      error: true,
      componentData: ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_10],
    };
  }
}
